import { Box, Tooltip, Typography, alpha } from "@mui/material";
import { memo, useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useAppTheme } from "@/hooks/useAppTheme";
import { ValidAny } from "@/types";
import { CellHighlight, CellHighlightMap } from "../../utils/sourcePreviewUtils";
import { SCHEMA_LABELS } from "../../utils/reviewUtils";

// =============================================================================
// Windowing configuration (#6755)
// =============================================================================

/**
 * Above this many sheet rows the body is windowed: only the visible slice is
 * put in the DOM, with spacer rows standing in for everything above and below.
 *
 * Below it, every row renders exactly as it always has — auto table layout,
 * natural column widths, no fixed row height. That is deliberate rather than
 * lazy. Windowing requires `table-layout: fixed` and a colgroup, because with
 * auto layout the browser sizes columns from the rows currently in the DOM and
 * they would visibly jump on every scroll. Small documents are the common case
 * and AC5 asks for them to be untouched, so they keep the old path.
 *
 * 2,000 rows is roughly where a full render stops being instant and is far
 * above any document a reviewer reads end to end.
 */
const VIRTUALISE_ABOVE_ROWS = 2000;

/** Fixed body row height in windowed mode. Must match the `& td` height below. */
const ROW_HEIGHT = 28;

/** Rows rendered beyond the viewport on each side, to cover fast scrolling. */
const OVERSCAN_ROWS = 12;

/**
 * Upper bound on rows rendered per pass, independent of measured heights —
 * insurance for the untested clientHeight arithmetic (see the span
 * computation). Roughly 4x a tall viewport at ROW_HEIGHT.
 */
const MAX_RENDERED_ROWS = 300;

const ROW_NUMBER_WIDTH = 48;

/** Column width in windowed mode, where auto sizing is unavailable. */
const VIRTUAL_COL_WIDTH = 150;

// =============================================================================
// Types
// =============================================================================

interface SpreadsheetPreviewProps {
  /** 2D array of raw cell values */
  data: ValidAny[][];
  /**
   * Highlights for an inclusive range of sheet rows.
   *
   * A function rather than a prebuilt map (#6755). The 497,711-record
   * motivating document produced ~497k highlight objects in a single `useMemo`
   * and froze the tab; this asks for the rows about to be painted.
   */
  highlightsFor: (startRow: number, endRow: number) => CellHighlightMap;
  /** Row index to scroll into view (0-indexed into data array) */
  focusRow: number | null;
  /** Header row index in the data array */
  headerRowIndex: number;
}

// =============================================================================
// Component
// =============================================================================

export const SpreadsheetPreview = ({ data, highlightsFor, focusRow, headerRowIndex }: SpreadsheetPreviewProps) => {
  const { theme, isDarkMode } = useAppTheme();
  const scrollRef = useRef<HTMLDivElement>(null);
  const focusRowRef = useRef<HTMLTableRowElement>(null);

  const virtualised = data.length > VIRTUALISE_ABOVE_ROWS;

  // Was recomputed on every render, which is a full pass over the sheet — the
  // one thing this component must not do per frame at 497k rows.
  const maxCols = useMemo(() => data.reduce((max, row) => Math.max(max, row?.length || 0), 0), [data]);

  // ── Body position ⇄ data index ───────────────────────────────────
  // The header row is rendered in <thead>, so the body skips it. Windowing
  // maths runs over contiguous body positions and converts, rather than
  // materialising an index array 497k entries long.
  const headerInBody = headerRowIndex >= 0 && headerRowIndex < data.length;
  const bodyRowCount = headerInBody ? data.length - 1 : data.length;

  const dataIndexAt = useCallback(
    (pos: number) => (headerInBody && pos >= headerRowIndex ? pos + 1 : pos),
    [headerInBody, headerRowIndex]
  );
  const bodyPositionOf = useCallback(
    (idx: number) => (headerInBody && idx > headerRowIndex ? idx - 1 : idx),
    [headerInBody, headerRowIndex]
  );

  // ── Visible window ───────────────────────────────────────────────
  const [range, setRange] = useState({ start: 0, end: 0 });

  const syncRange = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const first = Math.max(0, Math.floor(el.scrollTop / ROW_HEIGHT) - OVERSCAN_ROWS);
    // Hard cap on the rendered span. If clientHeight ever resolves to CONTENT
    // height rather than viewport height (a layout regression jsdom cannot
    // catch — it reports clientHeight 0), an uncapped span renders every row,
    // which is the exact freeze this component exists to prevent. 300 rows is
    // ~4x a tall viewport; hitting the cap degrades to blank rows on
    // over-scroll, not a hang.
    const span = Math.min(Math.ceil(el.clientHeight / ROW_HEIGHT) + OVERSCAN_ROWS * 2, MAX_RENDERED_ROWS);
    const last = Math.min(Math.max(bodyRowCount - 1, 0), first + span);
    // Returning `prev` unchanged skips the re-render entirely, so mid-row
    // scroll frames cost nothing.
    setRange((prev) => (prev.start === first && prev.end === last ? prev : { start: first, end: last }));
  }, [bodyRowCount]);

  useEffect(() => {
    if (!virtualised) return;
    const el = scrollRef.current;
    if (!el) return;
    syncRange();
    const observer = new ResizeObserver(syncRange);
    observer.observe(el);
    return () => observer.disconnect();
  }, [virtualised, syncRange]);

  // ── Highlights for what is on screen ─────────────────────────────
  const highlights = useMemo(() => {
    if (data.length === 0) return new Map() as CellHighlightMap;
    if (!virtualised) return highlightsFor(0, data.length - 1);
    return highlightsFor(dataIndexAt(range.start), dataIndexAt(range.end));
  }, [virtualised, highlightsFor, data.length, range.start, range.end, dataIndexAt]);

  // ── Scroll to the focused row ────────────────────────────────────
  // In windowed mode the target row is usually not in the DOM, so
  // `scrollIntoView` on a ref cannot work — scroll to its offset instead.
  useEffect(() => {
    if (focusRow === null || focusRow === undefined) return;

    // Small delay to ensure the drawer has finished opening
    const timer = setTimeout(() => {
      if (!virtualised) {
        focusRowRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }
      const el = scrollRef.current;
      if (!el) return;
      const centred = bodyPositionOf(focusRow) * ROW_HEIGHT - el.clientHeight / 2 + ROW_HEIGHT / 2;
      el.scrollTo({ top: Math.max(0, centred), behavior: "smooth" });
    }, 150);

    return () => clearTimeout(timer);
  }, [focusRow, virtualised, bodyPositionOf]);

  // ── Pre-compute background colors for each highlight status ──────
  // Avoids calling alpha() hundreds of times during render.
  const bgColors = useMemo(
    () => ({
      extracted: alpha(theme.palette.success.main, isDarkMode ? 0.15 : 0.08),
      error: alpha(theme.palette.error.main, isDarkMode ? 0.2 : 0.12),
      warning: alpha(theme.palette.warning.main, isDarkMode ? 0.15 : 0.1),
      unmapped: alpha(theme.palette.action.disabled, isDarkMode ? 0.1 : 0.06),
    }),
    [theme, isDarkMode]
  );

  if (data.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
        The spreadsheet appears to be empty.
      </Typography>
    );
  }

  const firstPosition = virtualised ? range.start : 0;
  const lastPosition = virtualised ? range.end : bodyRowCount - 1;
  const padTop = virtualised ? firstPosition * ROW_HEIGHT : 0;
  const padBottom = virtualised ? Math.max(0, (bodyRowCount - 1 - lastPosition) * ROW_HEIGHT) : 0;
  const renderedPositions = Math.max(0, lastPosition - firstPosition + 1);

  return (
    <Box
      ref={scrollRef}
      onScroll={virtualised ? syncRange : undefined}
      sx={{
        overflow: "auto",
        maxHeight: "100%",
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
      }}
    >
      <Box
        component="table"
        sx={{
          borderCollapse: "collapse",
          tableLayout: virtualised ? "fixed" : "auto",
          width: virtualised ? ROW_NUMBER_WIDTH + maxCols * VIRTUAL_COL_WIDTH : "100%",
          fontSize: "0.75rem",
          fontFamily: "monospace",
          "& th, & td": {
            border: 1,
            borderColor: "divider",
            px: 1,
            py: 0.5,
            whiteSpace: "nowrap",
            maxWidth: virtualised ? "none" : 200,
            overflow: "hidden",
            textOverflow: "ellipsis",
            ...(virtualised && { height: ROW_HEIGHT, boxSizing: "border-box" }),
          },
          "& th": {
            position: "sticky",
            top: 0,
            zIndex: 1,
            bgcolor: isDarkMode ? theme.palette.grey[800] : theme.palette.grey[100],
            fontWeight: 600,
          },
        }}
      >
        {/* Windowed mode cannot use auto column sizing — the browser would size
            from the rows currently in the DOM and the columns would jump. */}
        {virtualised && (
          <colgroup>
            <col style={{ width: ROW_NUMBER_WIDTH }} />
            {Array.from({ length: maxCols }, (_, colIdx) => (
              <col key={colIdx} style={{ width: VIRTUAL_COL_WIDTH }} />
            ))}
          </colgroup>
        )}
        <thead>
          <tr>
            {/* Row number header */}
            <Box
              component="th"
              sx={{
                width: ROW_NUMBER_WIDTH,
                textAlign: "center",
                color: "text.disabled",
                bgcolor: isDarkMode ? `${theme.palette.grey[900]} !important` : `${theme.palette.grey[200]} !important`,
              }}
            >
              #
            </Box>
            {/* Column headers from the header row */}
            {Array.from({ length: maxCols }, (_, colIdx) => {
              const headerValue = data[headerRowIndex]?.[colIdx];
              return (
                <th key={colIdx}>
                  {headerValue !== null && headerValue !== undefined && String(headerValue).trim() !== ""
                    ? String(headerValue)
                    : `Col ${colIdx + 1}`}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {padTop > 0 && <SpacerRow height={padTop} colSpan={maxCols + 1} />}

          {Array.from({ length: renderedPositions }, (_, offset) => {
            const rowIdx = dataIndexAt(firstPosition + offset);
            if (rowIdx >= data.length) return null;

            const row = data[rowIdx];
            const isFocusRow = focusRow === rowIdx;
            const isGlobalRow = rowIdx < headerRowIndex;

            return (
              <Box
                component="tr"
                key={rowIdx}
                ref={isFocusRow ? focusRowRef : undefined}
                sx={{
                  ...(virtualised && { height: ROW_HEIGHT }),
                  ...(isFocusRow && {
                    outline: `2px solid ${theme.palette.primary.main}`,
                    outlineOffset: -1,
                  }),
                  ...(isGlobalRow && {
                    bgcolor: isDarkMode ? alpha(theme.palette.info.main, 0.05) : alpha(theme.palette.info.main, 0.03),
                  }),
                }}
              >
                {/* Row number */}
                <Box
                  component="td"
                  sx={{
                    textAlign: "center",
                    color: "text.disabled",
                    bgcolor: isDarkMode ? theme.palette.grey[900] : theme.palette.grey[200],
                    fontWeight: isFocusRow ? 700 : 400,
                  }}
                >
                  {rowIdx + 1}
                </Box>

                {/* Data cells */}
                {Array.from({ length: maxCols }, (_, colIdx) => {
                  const cellValue = row?.[colIdx];
                  const highlightKey = `${rowIdx}:${colIdx}`;
                  const highlight = highlights.get(highlightKey);

                  return <CellRenderer key={colIdx} value={cellValue} highlight={highlight} bgColors={bgColors} />;
                })}
              </Box>
            );
          })}

          {padBottom > 0 && <SpacerRow height={padBottom} colSpan={maxCols + 1} />}
        </tbody>
      </Box>
    </Box>
  );
};

// =============================================================================
// Spacer Row — stands in for the rows outside the window so the scrollbar
// reflects the whole sheet. Inline styles, to escape the table's `& td` rules.
// =============================================================================

const SpacerRow = ({ height, colSpan }: { height: number; colSpan: number }) => (
  <tr aria-hidden style={{ height }}>
    <td colSpan={colSpan} style={{ height, padding: 0, border: "none" }} />
  </tr>
);

// =============================================================================
// Pre-computed background colors (passed from parent to avoid per-cell alpha())
// =============================================================================

interface BgColors {
  extracted: string;
  error: string;
  warning: string;
  unmapped: string;
}

// =============================================================================
// Cell Renderer — memoized to prevent re-renders on parent state changes
// =============================================================================

const CellRenderer = memo(
  ({ value, highlight, bgColors }: { value: ValidAny; highlight: CellHighlight | undefined; bgColors: BgColors }) => {
    const cellContent = value !== null && value !== undefined && String(value).trim() !== "" ? String(value) : "";
    const bgColor = highlight ? bgColors[highlight.status] : undefined;
    const hasTooltipContent = highlight && (highlight.fields.length > 0 || highlight.errors.length > 0);

    if (!hasTooltipContent) {
      return (
        <Box
          component="td"
          sx={{
            bgcolor: bgColor,
            ...(highlight?.status === "error" && { fontWeight: 600 }),
          }}
        >
          {cellContent}
        </Box>
      );
    }

    // Lazy tooltip — only mount Tooltip tree on hover
    return (
      <LazyTooltipCell
        cellContent={cellContent}
        bgColor={bgColor}
        isError={highlight.status === "error"}
        highlight={highlight}
      />
    );
  }
);

CellRenderer.displayName = "CellRenderer";

// =============================================================================
// Lazy Tooltip Cell — defers Tooltip mount until first hover, then lets
// MUI's built-in hover handling manage open/close from that point on.
// =============================================================================

const LazyTooltipCell = ({
  cellContent,
  bgColor,
  isError,
  highlight,
}: {
  cellContent: string;
  bgColor: string | undefined;
  isError: boolean;
  highlight: CellHighlight;
}) => {
  const [activated, setActivated] = useState(false);
  const handleEnter = useCallback(() => setActivated(true), []);

  const cell = (
    <Box
      component="td"
      onMouseEnter={!activated ? handleEnter : undefined}
      sx={{
        bgcolor: bgColor,
        cursor: "help",
        ...(isError && { fontWeight: 600 }),
      }}
    >
      {cellContent}
    </Box>
  );

  // Before first hover — plain cell, no Tooltip in the tree
  if (!activated) return cell;

  // After first hover — Tooltip stays mounted and handles its own open/close
  return (
    <Tooltip title={<CellTooltipContent highlight={highlight} />} arrow placement="top">
      <Box
        component="td"
        sx={{
          bgcolor: bgColor,
          cursor: "help",
          ...(isError && { fontWeight: 600 }),
        }}
      >
        {cellContent}
      </Box>
    </Tooltip>
  );
};

// =============================================================================
// Tooltip Content
// =============================================================================

const CellTooltipContent = ({ highlight }: { highlight: CellHighlight }) => (
  <Box sx={{ fontSize: "0.75rem" }}>
    {highlight.fields.map((f, i) => (
      <Box key={i}>
        <Typography variant="caption" sx={{ fontWeight: 600 }}>
          {SCHEMA_LABELS[f.targetSchema] || f.targetSchema}.{f.targetField}
        </Typography>
      </Box>
    ))}
    {highlight.errors.map((e, i) => (
      <Box key={`err-${i}`} sx={{ color: e.severity === "error" ? "error.light" : "warning.light", mt: 0.25 }}>
        <Typography variant="caption">
          {e.severity.toUpperCase()}: {e.message}
        </Typography>
      </Box>
    ))}
  </Box>
);
